import { _ } from "../../core/polyglot/polyglot.js";
import {
	findBestFormatForNumbers,
	formatCurrency,
} from "../../core/helpers/currency-formatting-util.js";
import {
	formatAsDayAndMonth,
	formatAsMonth,
	formatAsWeekNumber,
} from "../../core/helpers/date-formatting-util.js";
import { getCSSVariable } from "../../core/helpers/css-util.js";
import chartBuilder from "../../core/charts.js";
import HighchartWidget from "../../core/widgets/HighchartWidget.js";
import LegendWidget from "../../core/widgets/LegendWidget.js";
import periodSelection from "../../core/charts/periodSelection.js";
import responsive from "../../core/responsive.js";
import session from "../../core/session.js";

/**
 * `businessActivityChartWidget` displays day-by-bay (or other
 * period types selected by the user) in the period, the positive
 * and negative changes that affects the result as bars and the
 * accumulated result as a line.
 *
 * The user can decide not to display credit records, each day
 * will then display the sum of the changes.
 *
 * There are the following types of changes: income, costs,
 * financial items, appropriations, taxes and profit.
 *
 * @param {boolean} [spec.isLimited] - Set this to true if you want the
 * receiver to hide transaction details.
 * @param {boolean} [spec.shouldHideAppropriations] - Set this to true
 * if you want the receiver to hide "Appropriations" data and legend.
 */
export default class BusinessActivityChartWidget extends HighchartWidget {
	constructor(spec = {}) {
		super(...arguments);

		this._periods = [];

		this._isLimited = spec.isLimited;
		const shouldHideAppropriations = spec.shouldHideAppropriations;

		this._formatter = formatCurrency;

		this.onClickPoint = this._events.createEvent();
		this.onClickBand = this._events.createEvent();

		this._chartId = "business-activity-chart";
		this._periodSelection = periodSelection();

		this._legend = new LegendWidget({
			chart: this,
			legendId: "business-activity-chart-legend",
			legendClass: "business-activity",
			itemOptions: [
				{
					title: _("Income"),
					seriesId: "income",
					pairedTo: "creditedIncome",
					valueRenderer: (value) => this._formatter(value),
				},
				{
					title: _("Costs"),
					seriesId: "costs",
					pairedTo: "creditedCosts",
					valueRenderer: (value) => this._formatter(value),
				},
				{
					title: _("Financial items"),
					seriesId: "financialIncome",
					pairedTo: "financialCosts",
					valueRenderer: (value) => this._formatter(value),
				},
				{
					title: _("Appropriations"),
					seriesId: "incomeClosingEntries",
					pairedTo: "costsClosingEntries",
					valueRenderer: (value) => this._formatter(value),
					isHidden: shouldHideAppropriations,
				},
				{
					title: _("Taxes"),
					seriesId: "costsTaxes",
					pairedTo: "incomeTaxes",
					valueRenderer: (value) => this._formatter(value),
				},
				{
					titleRenderer: (value) => (html) => {
						if (value > 0) {
							html.render(_("Profit"));
						} else {
							html.render(_("Loss"));
						}
					},
					seriesId: "profit",
					valueRenderer: (value) => this._formatter(value),
				},
			],
		});
	}

	show(options) {
		this._setup();
		this._updateHighchartsDiv();
		const businessActivity = options.businessActivity;
		const values = businessActivity.getHighChartsSeries();

		this._periods = values.periods;
		this._updateXAxis();

		this.setData([
			values.incomeTaxes,
			values.costsTaxes,
			values.incomeClosingEntries,
			values.costsClosingEntries,
			values.creditedCosts,
			values.creditedIncome,
			values.financialIncome,
			values.financialCosts,
			values.income,
			values.costs,
			values.accumulatedResult,
		]);

		this._formatter = findBestFormatForNumbers(
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			this._data.flatMap((values) => values.map((item) => item.y)),
		);

		this._legend.setValues(businessActivity.getLegendValues());
	}

	_setup() {
		const incomeColor = getCSSVariable(
			"--kpi-business-activity-income-color",
		);
		const financialItemsColor = getCSSVariable(
			"--kpi-financial-items-color",
		);
		const costsColor = getCSSVariable("--kpi-costs-color");
		const taxesColor = getCSSVariable("--kpi-taxes-color");
		const profitColor = getCSSVariable(
			"--kpi-business-activity-profit-color",
		);
		const appropriationsColor = getCSSVariable(
			"--kpi-appropriations-color",
		);

		this._series = [
			{
				id: "incomeTaxes",
				name: _("Income tax").toString(),
				label: _("Income tax").toString(),
				legendIndex: 9,
				color: taxesColor,
				stack: "stack",
				showInLegend: false,
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "costsTaxes",
				name: _("Tax").toString(),
				label: _("Costs tax").toString(),
				legendIndex: 10,
				color: taxesColor,
				stack: "stack",
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "incomeClosingEntries",
				name: _("Closing entries").toString(),
				label: _("Positive closing entries").toString(),
				legendIndex: 7,
				color: appropriationsColor,
				stack: "stack",
				showInLegend: false,
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "costsClosingEntries",
				name: _("Closing entries").toString(),
				label: _("Negative closing entries").toString(),
				legendIndex: 8,
				color: appropriationsColor,
				stack: "stack",
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "creditedCosts",
				name: _("Credited costs").toString(),
				label: _("Credited costs").toString(),
				legendIndex: 5,
				color: costsColor,
				stack: "stack",
				showInLegend: false,
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "creditedIncome",
				name: _("Credited income").toString(),
				label: _("Credited income").toString(),
				legendIndex: 6,
				color: incomeColor,
				stack: "stack",
				showInLegend: false,
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "financialIncome",
				name: _("Financial entries").toString(),
				label: _("Financial income").toString(),
				legendIndex: 3,
				color: financialItemsColor,
				stack: "stack",
				showInLegend: false,
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "financialCosts",
				name: _("Financial entries").toString(),
				label: _("Financial costs").toString(),
				legendIndex: 4,
				color: financialItemsColor,
				stack: "stack",
			},
			{
				id: "income",
				name: _("Income").toString(),
				label: _("Income").toString(),
				legendIndex: 1,
				color: incomeColor,
				stack: "stack",
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "costs",
				name: _("Costs").toString(),
				label: _("Costs").toString(),
				legendIndex: 2,
				color: costsColor,
				stack: "stack",
				dataLabels: {
					formatter: (value) => this._formatter(value),
				},
			},
			{
				id: "profit",
				name: _("Profit").toString(),
				label: _("Profit").toString(),
				legendIndex: 11,
				type: "scatter",
				showInLegend: true,
				color: profitColor,
				borderWidth: 0,
				lineWidth: 3,
				lineColor: profitColor,
				dataLabels: {
					enabled: false,
				},
				marker: {
					symbol: "circle",
					lineWidth: responsive.isMobileBreakpoint() ? 1 : 3,
					lineColor: profitColor,
					fillColor: "#ffffff",
					radius: responsive.isMobileBreakpoint() ? 3 : 6,
				},
			},
		];

		this._chartOptions = this._buildChartOptions();
	}

	_buildChartOptions() {
		const that = this;

		return chartBuilder()
			.set("xAxis", this._buildXAxisOptions())
			.set("yAxis", chartBuilder.yAxis())
			.set("plotOptions", {
				series: chartBuilder
					.seriesPlotOptions()
					.set({
						stacking: "normal",
						dataLabels: {
							enabled: false,
						},
					})
					.setSelectablePoints({
						periodSelection: this._periodSelection,
						onClick: (point) => this.onClickPoint.trigger(point),
					})
					.build(),
			})
			.setTooltip({
				formatter: function () {
					const highchartObject = this;
					return that._tooltipFormatter(highchartObject);
				},
			})
			.set("series", this._series)
			.build();
	}

	_buildXAxisOptions() {
		const xAxis = chartBuilder.xDateAxis();
		if (this._periods.length) {
			xAxis.setPeriods({
				periods: this._periods,
			});
		}

		if (!this._isLimited) {
			xAxis.setSelectablePeriods({
				periodSelection: this._periodSelection,
				plotBandClicked: (plotBand) => {
					if (!plotBand.options.period) {
						return;
					}
					this.onClickPoint.trigger(plotBand);
				},
			});
		}

		return xAxis
			.set({
				id: "business-activity-xaxis",
				tickPixelInterval: responsive.isMobileBreakpoint() ? 20 : 40,
				labels: {
					rotation: responsive.isMobileBreakpoint() ? -90 : -45,
					style: {
						paddingLeft: responsive.isMobileBreakpoint()
							? "50px"
							: "0px",
						paddingTop: responsive.isMobileBreakpoint()
							? "4px"
							: "0px",
					},
					y: 20,
				},
			})
			.setDateFormat({
				Month: formatAsMonth,
				Week: this._renderWeek.bind(this),
				Day: formatAsDayAndMonth,
			})
			.setTickIntervalFromPeriodType({
				Day: () => {
					if (session.getPeriod().getPeriodType() === "Year") {
						return xAxis.setMonthTickInterval();
					}
					if (
						session.getPeriod().getPeriodType() === "Month" &&
						!responsive.isMobileBreakpoint()
					) {
						// Avoid showing every day for mobiles. The interval gets too tight.
						return xAxis.setDayTickInterval();
					}
					return xAxis.setWeekTickInterval();
				},
			});
	}

	_updateXAxis() {
		this._chartOptions.xAxis = this._buildXAxisOptions().build();
		if (this._highchart) {
			this._highchart
				.get("business-activity-xaxis")
				.update(this._chartOptions.xAxis);
		}
	}

	_renderWeek(weekDay) {
		return _("Week {number}", { number: formatAsWeekNumber(weekDay) });
	}

	_formatSerieName(point) {
		const series = point.series;
		if (series.name === _("Profit").toString() && point.y < 0) {
			return _("Loss");
		}

		return series.name;
	}

	_tooltipFormatter(highchartObject) {
		let tooltip = `<b>${highchartObject.point.period.toSmartString()}</b><br/>`;
		const x = highchartObject.x.valueOf();

		let points = highchartObject.series.chart.series.map((series) => {
			if (!series.visible) {
				return undefined;
			}
			return series.data.find((data) => data.x.valueOf() === x);
		});

		points = points.filter((point) => point && point.y !== 0);

		if (!points.length) {
			return false;
		}

		tooltip += points
			.map(
				(point) =>
					`<span style="color:${
						point.series.color
					}">\u25CF</span> ${this._formatSerieName(
						point,
					)}: <b> ${formatCurrency(point.y)}</b><br/>`,
			)
			.join("<br/>");

		return tooltip;
	}
}
